import type { ReactNode } from 'react';
import { Plus } from '@/components/icons';
import { Chip, type ChipTone } from '@/components/ui/chip';
import {
  stateChips,
  type StateChip,
  type StateChipKind,
  type StateChipSnapshot,
} from '@/domain/state-chips';
import { Msg } from '@/lib/locale-context';
import styles from './state-chips.module.css';

/**
 * The state chip (docs/CLOSED_AXIS.md "Quiet defaults"): the word an axis
 * wears when it has news - Active, Delivered, the verdict, Closed - as the kit
 * `Chip` in its OUTLINE look, dotless (docs/DESIGN_SYSTEM.md "Chip", ruled
 * 2026-10-20: an outline is a state word on a line that edits it). The law
 * lives in `domain/state-chips.ts`; this file only draws it, in its forms:
 *
 * - `StateChipRun`: the display-only run a SUMMARY row speaks (the list
 *   table's `state` column, 2026-09-15) - chronological, silent at rest, and
 *   able to leave out the one axis a section heading already states.
 * - `StateChipButton`: the same chip as a menu trigger, the item page's
 *   state line (`items/[itemId]/lifecycle-control.tsx` hangs the acts).
 * - `AddStateChip`: the run's closing "+", the state line's one way to add an
 *   axis that has no chip yet (`items/[itemId]/add-state-control.tsx`).
 *
 * Never the tint and never a dot: the dot and the tint are health's alone
 * (HEALTH_SIGNALS §5), and a state word must not be mistaken for a health
 * verdict. A tone reaches the word and the edge only.
 */

/** `sm` is a summary row's size (the list table's cells, the glyph's tip).
 *  `line` is the item page's state line, and the default is the chip as it is
 *  drawn anywhere else: both are the kit's `md`, so a display chip on the line
 *  (a closed item's verdict) stands as tall as the triggers beside it. */
export type StateChipSize = 'sm' | 'line';

/** `data-*` attributes: a surface's test hooks and the axis's own state
 *  (`data-closed`, `data-verdict`), on the chip's root. */
type ChipData = { [key: `data-${string}`]: string | number | boolean | undefined };

/** The axis's colour voice as a kit tone: the TEXT grade for the word and a
 *  mixed edge, by the two-reds contract (the kit's `critical`). */
const TONE: Record<StateChip['tone'], ChipTone> = {
  plain: 'neutral',
  good: 'good',
  alarm: 'critical',
};

/** The chip's word, with the verdict's dim axis name ahead of it. */
function Word({ chip }: { chip: StateChip }) {
  return (
    <>
      {chip.axis && (
        <span className={styles.axis}>
          <Msg message={chip.axis} />
        </span>
      )}
      <Msg message={chip.label} />
    </>
  );
}

/** The display chip: no act behind it. A surface keeps its test hooks and data
 *  attributes on an axis that has nothing left to edit (a closed item's
 *  verdict). On the state line it passes `size="line"`. */
export function StateChipPill({
  chip,
  size,
  ...data
}: { chip: StateChip; size?: StateChipSize } & ChipData) {
  return (
    <Chip
      size={size === 'sm' ? 'sm' : 'md'}
      tone={TONE[chip.tone]}
      look="outline"
      data-kind={chip.kind}
      {...data}
    >
      <Word chip={chip} />
    </Chip>
  );
}

/**
 * A summary row's state: the chips an item wears, chronologically, and
 * NOTHING at rest (an untouched row renders no element at all). `hide` names
 * the one axis the surrounding section heading already states, so the row
 * does not repeat it (the grouped dimension leaves the row, docs/VIEWS.md
 * §3a); the other chips stay.
 */
export function StateChipRun({
  snapshot,
  hide,
  size,
  className,
}: {
  snapshot: StateChipSnapshot;
  hide?: StateChipKind | null;
  size?: StateChipSize;
  className?: string;
}) {
  const chips = stateChips(snapshot).filter((c) => c.kind !== hide);
  if (chips.length === 0) return null;
  return (
    <span
      className={className ? `${styles.run} ${className}` : styles.run}
      data-testid="state-chips"
    >
      {chips.map((chip) => (
        <StateChipPill key={chip.kind} chip={chip} size={size} />
      ))}
    </span>
  );
}

/** The interactive chip: the same word as the trigger of its axis's menu, on
 *  the item page's state line. `trail` follows the word (a finding's sign);
 *  the caret is the kit's, on a fine pointer only. `pending` disables it at
 *  the tap and dims it after 400ms (2026-09-16). */
export function StateChipButton({
  chip,
  onClick,
  open,
  pending,
  trail,
  testId,
  ...data
}: {
  chip: StateChip;
  onClick: () => void;
  /** Whether its menu is open (`aria-expanded`). */
  open: boolean;
  pending?: boolean;
  trail?: ReactNode;
  testId?: string;
} & ChipData) {
  return (
    <Chip
      size="md"
      tone={TONE[chip.tone]}
      look="outline"
      onClick={onClick}
      popup={{ open }}
      caret
      pending={pending}
      trail={trail}
      testId={testId}
      {...data}
    >
      <Word chip={chip} />
    </Chip>
  );
}

/**
 * The state line's "Add a state" trigger (Leon, 2026-09-16): the chip reduced
 * to a circle around a "+", closing the run of chips. The kit's icon-only chip
 * is as tall as the chips beside it at every pointer, so the line stays one
 * height.
 */
export function AddStateChip({
  onClick,
  open,
  pending,
  label,
  testId,
}: {
  onClick: () => void;
  /** Whether its menu is open (`aria-expanded`). */
  open: boolean;
  pending?: boolean;
  /** Its name and its tooltip: the surface's catalog sentence. */
  label: string;
  testId?: string;
}) {
  return (
    <Chip
      size="md"
      look="outline"
      iconOnly
      ariaLabel={label}
      title={label}
      onClick={onClick}
      popup={{ open }}
      pending={pending}
      testId={testId}
    >
      <Plus aria-hidden />
    </Chip>
  );
}
